import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {AppRoutingModule} from './app-routing.module';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AppComponent} from './app.component';
import {LoginComponent} from './modules/login/login.component';
import {AuthenticationService} from './modules/common/services/authentication/authentication.service';
import {
  AccountAlreadyConfirmedComponent
} from './modules/account/account-already-confirmed/account-already-confirmed.component';
import {AccountConfirmComponent} from './modules/account/account-confirm/account-confirm.component';
import {AccountErrorComponent} from './modules/account/account-error/account-error.component';
import {AccountCreatedComponent} from './modules/account/account-created/account-created.component';
import {ApiRouteConfig} from './config/api.route.config';
import {AuthGuard} from './modules/common/services/authorization/authGuard';
import {IsLoggedService} from './modules/common/services/is-logged.service';
import {AppLanguageService} from './i18n/app.language.service';
import {LoaderService} from './loader/loader.service';
import {LoaderComponent} from './loader/loader.component';
import {LoggerService} from './logger/LoggerService';
import {AppConfig} from './config/app.config';
import {MaintenancePageComponent} from './modules/common/maintenance-page/maintenance-page.component';
import {MDBBootstrapModulesPro, MDBSpinningPreloader, ToastModule, ToastService} from 'ng-uikit-pro-standard';
import {ForgotPasswordComponent} from './modules/login/forgot-password/forgot-password.component';
import {ResetPasswordComponent} from './modules/account/reset-password/reset-password.component';
import {NgSelectModule} from '@ng-select/ng-select';
import {CommonsModule} from './modules/common/common.module';
import {AppNavbarComponent} from './app-navbar/app-navbar.component';
import {AppAvailableLanguagesService} from './app-available-languages.service';
import {PermissionsGuard} from './modules/common/services/authorization/permissionsGuard';
import {ForbiddenPageComponent} from './modules/common/forbidden-page/forbidden-page.component';
import {CountriesService} from './modules/common/countries.service';
import {StatesService} from './modules/common/states.service';
import {RegisterComponent} from './modules/register/register.component';
import {AlreadyLoggedGuard} from './modules/common/services/authorization/already-logged.guard';
import {
  AlreadyLoggedLoginComponentGuard
} from './modules/common/services/authorization/AlreadyLoggedLoginComponentGuard';
import {HomeComponent} from './home/home.component';
import {SubscriptionService} from './modules/common/services/subscription.service';
import {FeedbackComponent} from './modules/feedback/feedback.component';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {BusinessRulesService} from './services/business-rules.service';
import {TokenService} from './modules/common/services/token.service';
import {CookieService} from 'ngx-cookie-service';
import {BusinessSettings} from './config/business-settings';
import {Localization} from './modules/common/enums/localization';
import {GlobalErrorHandler} from './config/global-error-handler';
import {MatBadgeModule} from '@angular/material/badge';
import {PermissionsService} from './modules/common/services/permissions-service';
import {LocalizationService} from './services/localization.service';
import {ChappsCommonAngularLibraryModule, FreshdeskWidgetModule} from '@npm.chapps.com/chapps.common.angular.library';
import {ILocalization} from '@npm.chapps.com/chapps.common.angular.library/lib/interface/ILocalization';
import {AppContextRedirectConfig} from './config/app-context-redirect-config';
import {DirectiveModule} from './modules/common/directives/directive.module';
import {LicenseService} from "./modules/license/license.service";
import {LicenseComponent} from "./modules/license/license.component";
import {ResidentsModule} from "./modules/residents/residents.module";
import {SystemNotificationsModule} from "./modules/system-notifications/system-notifications.module";
import {MaintenanceModule} from "./modules/maintenance/maintenance.module";
import {AngularSvgIconModule} from "angular-svg-icon";
import {SystemUpgradeScreenModule} from "./modules/support/system/system-upgrade-screen/system-upgrade-screen.module";
import {DebugMenuModule} from "./modules/support/debugging/debug-menu/debug-menu.module";
import {LanguageSwitcherModule} from "./i18n/language-switcher/language-switcher.module";
import {TreeModule} from '@ali-hm/angular-tree-component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatTabsModule} from '@angular/material/tabs';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {EnvironmentModule} from "./environment/environment.module";
import {Environment} from "./environment/environment";


export function createTranslateLoader(http: HttpClient, environment: Environment) {
  return new TranslateHttpLoader(http, environment.apiUrl + 'api/translations/', '.json');
}

@NgModule({
    exports: [],
    declarations: [
        AppComponent,
        LoginComponent,
        LicenseComponent,
        AccountAlreadyConfirmedComponent,
        AccountConfirmComponent,
        AccountErrorComponent,
        AccountCreatedComponent,
        LoaderComponent,
        ForbiddenPageComponent,
        MaintenancePageComponent,
        ForgotPasswordComponent,
        ResetPasswordComponent,
        AppNavbarComponent,
        RegisterComponent,
        HomeComponent,
        FeedbackComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        EnvironmentModule,
        MatTooltipModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient, Environment]
            }
        }),
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        HttpClientModule,
        ToastModule.forRoot(),
        MDBBootstrapModulesPro.forRoot(),
        NgSelectModule,
        CommonsModule.forRoot(),
        LeafletModule,
        ChappsCommonAngularLibraryModule
            .forRoot(Localization as ILocalization, AppConfig.PRIMARY_COLOR, AppConfig.SECONDARY_COLOR, AppContextRedirectConfig.CONFIG),
        TreeModule,
        MatTabsModule,
        MatBadgeModule,
        DirectiveModule,
        MatSnackBarModule,
        FreshdeskWidgetModule,
        ResidentsModule,
        SystemNotificationsModule,
        MaintenanceModule,
        AngularSvgIconModule.forRoot(),
        SystemNotificationsModule,
        SystemUpgradeScreenModule,
        DebugMenuModule,
        LanguageSwitcherModule,
    ],
    providers: [
        AppLanguageService,
        ApiRouteConfig,
        IsLoggedService,
        LoaderService,
        LoggerService,
        AppConfig,
        ToastService,
        AuthenticationService,
        AuthGuard,
        AlreadyLoggedGuard,
        AlreadyLoggedLoginComponentGuard,
        PermissionsGuard,
        AppAvailableLanguagesService,
        CountriesService,
        StatesService,
        SubscriptionService,
        MDBSpinningPreloader,
        BusinessRulesService,
        TokenService,
        PermissionsService,
        CookieService,
        BusinessSettings,
        LocalizationService,
        LicenseService,
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }

